import { Box } from '@mui/material';
import {
  AddCircle,
  Cancel,
  ErrorOutline,
  HelpOutline,
  Replay,
  Send,
  Sync,
  Webhook,
} from '@mui/icons-material';
import {
  AuditEvent,
  AuditEventNameType,
} from 'api/giftcard-ach-service/giftcard-ach-service.types';
import {
  EventTimelineEntry,
  EventTimelineVisual,
} from 'components/event-timeline/event-timeline.types';

const getEventNameVisuals = (
  t: (key: string) => string,
): Record<AuditEventNameType, EventTimelineVisual> => ({
  CREATED: {
    label: t('drawer.achTransactionsEvents.timeline.eventNames.created'),
    icon: <AddCircle fontSize="small" />,
    severity: 'info',
  },
  SUBMITTED_TO_DWOLLA: {
    label: t('drawer.achTransactionsEvents.timeline.eventNames.submitted'),
    icon: <Send fontSize="small" />,
    severity: 'primary',
  },
  SUBMITTION_TO_DWOLLA_FAILED: {
    label: t(
      'drawer.achTransactionsEvents.timeline.eventNames.submissionToDwollaFailed',
    ),
    icon: <ErrorOutline fontSize="small" />,
    severity: 'error',
  },
  WEBHOOK_UPDATE: {
    label: t('drawer.achTransactionsEvents.timeline.eventNames.webhookUpdate'),
    icon: <Webhook fontSize="small" />,
    severity: 'secondary',
  },
  WEBHOOK_UPDATE_FAILED: {
    label: t(
      'drawer.achTransactionsEvents.timeline.eventNames.webhookUpdateFailed',
    ),
    icon: <ErrorOutline fontSize="small" />,
    severity: 'error',
  },
  CANCELLATION_STARTED: {
    label: t(
      'drawer.achTransactionsEvents.timeline.eventNames.cancellationStarted',
    ),
    icon: <Sync fontSize="small" />,
    severity: 'warning',
  },
  CANCELLED: {
    label: t('drawer.achTransactionsEvents.timeline.eventNames.cancelled'),
    icon: <Cancel fontSize="small" />,
    severity: 'warning',
  },
  CANCELLATION_FAILED: {
    label: t(
      'drawer.achTransactionsEvents.timeline.eventNames.cancellationFailed',
    ),
    icon: <ErrorOutline fontSize="small" />,
    severity: 'error',
  },
  RETRY_TRANSFER_CREATED: {
    label: t(
      'drawer.achTransactionsEvents.timeline.eventNames.retryTransferCreated',
    ),
    icon: <Replay fontSize="small" />,
    severity: 'info',
  },
});

const getFallbackVisual = (eventName: string): EventTimelineVisual => ({
  label: eventName,
  icon: <HelpOutline fontSize="small" />,
  severity: 'secondary',
});

const isPayloadNote = (note: string) => note.includes('{');

const getNote = (additionalDetails: string) => {
  if (!additionalDetails) return undefined;

  if (!isPayloadNote(additionalDetails)) return additionalDetails;

  return (
    <Box
      component="span"
      sx={{
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize: '12px',
      }}
    >
      {additionalDetails}
    </Box>
  );
};

export const mapAuditEventsToTimelineEntries = (
  events: AuditEvent[],
  t: (key: string) => string,
): EventTimelineEntry[] => {
  const visuals = getEventNameVisuals(t);

  return events.map(event => ({
    id: event.id,
    date: event.eventTime,
    visual: visuals[event.eventName] ?? getFallbackVisual(event.eventName),
    note: getNote(event.additionalDetails),
  }));
};
